import React, { useState } from 'react';
import Icon from './icons';
import { tx } from './i18n';
import { RIG_PRESETS, BAUD_RATES, isWebSerialSupported, savedRigConfig, connectRig, disconnectRig, detectRig, useWebSerialRig,
  sUnitsLabel, setTxAllowed, rigPtt, rigSplitOff } from './webSerialCat';
import { ROTOR_PRESETS, ROTOR_BAUDS, savedRotorConfig, connectRotor, disconnectRotor, rotorStop, useWebSerialRotor } from './webSerialRotor';

function Head({ status, title, expanded, onToggle }) {
  return (
    <button type="button" className="webserial__head" onClick={onToggle}>
      <span className={'webserial__dot webserial__dot--' + status} />
      <span className="webserial__title">{title}</span>
      <span className="webserial__state">
        {status === 'connected' ? tx('połączono') : status === 'connecting' ? tx('łączenie…') : ''}
      </span>
      <span className={'webserial__chev' + (expanded ? ' webserial__chev--open' : '')}><Icon name="chevronDown" size={14} /></span>
    </button>
  );
}

// ── Radio przez USB bezposrednio z przegladarki (Web Serial) -- bez agenta ────
export default function WebSerialPanel({ notify }) {
  const rig = useWebSerialRig();
  const saved = savedRigConfig();
  const [presetId, setPresetId] = useState(saved?.presetId || 'ic7300');
  const preset = RIG_PRESETS.find(p => p.id === presetId) || RIG_PRESETS[0];
  const [baud, setBaud] = useState(saved?.baud || preset.baud);
  const [civ, setCiv] = useState(((saved?.civ ?? preset.civ) ?? 0x94).toString(16).toUpperCase());
  const [expanded, setExpanded] = useState(false);
  const active = rig.status !== 'disconnected';
  const detecting = rig.detecting != null;

  const pickPreset = (id) => {
    const p = RIG_PRESETS.find(x => x.id === id);
    setPresetId(id);
    setBaud(p.baud);
    if (p.civ != null) setCiv(p.civ.toString(16).toUpperCase());
  };

  const connect = async () => {
    const civNum = parseInt(civ, 16);
    if (preset.protocol === 'icom' && !(civNum > 0 && civNum < 0xE0)) { notify(tx('Nieprawidłowy adres CI-V (hex, np. 94)'), 'error'); return; }
    try {
      await connectRig({ presetId, baud: Number(baud), civ: preset.protocol === 'icom' ? civNum : undefined });
    } catch (e) {
      notify(tx('Nie udało się otworzyć portu: {0}', [e.message || String(e)]), 'error');
    }
  };

  const detect = async () => {
    try {
      const found = await detectRig();
      if (!found) return;
      const p = RIG_PRESETS.find(x => x.id === found.presetId);
      setPresetId(found.presetId); setBaud(found.baud);
      if (found.civ != null) setCiv(found.civ.toString(16).toUpperCase());
      notify(tx('Wykryto radio: {0}, {1} bodów', [p ? p.label : found.presetId, found.baud]));
    } catch (e) {
      notify(tx('Nie udało się otworzyć portu: {0}', [e.message || String(e)]), 'error');
    }
  };

  const toggleTx = (on) => {
    if (on && !window.confirm(tx('Po włączeniu logger będzie mógł uruchamiać nadajnik: przycisk PTT i makra CW (F7–F12) wysyłane do radia. Nadajesz zawsze Ty — klikając. Włączyć?'))) return;
    setTxAllowed(on);
  };
  const ptt = (on) => rigPtt(on).catch(e => notify(e.message || String(e), 'error'));

  if (!isWebSerialSupported()) {
    return (
      <div className="webserial webserial--off">
        <Icon name="radio" size={14} /> {window.isSecureContext
          ? tx('Radio przez USB bez agenta działa w Chrome, Edge lub Operze na komputerze.')
          : tx('Radio przez USB bez agenta wymaga połączenia HTTPS.')}
      </div>
    );
  }

  return (
    <div className="webserial">
      <Head status={rig.status} title={tx('Radio przez USB (bez agenta)')} expanded={expanded} onToggle={() => setExpanded(x => !x)} />
      {(expanded || active || detecting) && (
        <div className="webserial__body">
          {!active && (
            <>
              <button type="button" className="btn btn--primary btn--sm" style={{ width: '100%' }} onClick={detect} disabled={detecting}>
                <Icon name="search" size={14} /> {detecting ? tx('Szukam radia… {0} bodów', [rig.detecting]) : tx('Wykryj radio automatycznie')}
              </button>
              <div className="webserial__hint">{tx('albo wybierz ręcznie:')}</div>
              <select className="input webserial__select" value={presetId} onChange={e => pickPreset(e.target.value)} disabled={detecting}>
                {RIG_PRESETS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
              <div className="webserial__row">
                <label>{tx('Prędkość')}
                  <select className="input" value={baud} onChange={e => setBaud(Number(e.target.value))}>
                    {BAUD_RATES.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </label>
                {preset.protocol === 'icom' && (
                  <label>{tx('Adres CI-V')}
                    <input className="input" value={civ} maxLength={2} onChange={e => setCiv(e.target.value.replace(/[^0-9a-f]/gi, ''))} />
                  </label>
                )}
              </div>
              <div className="webserial__hint">
                {tx('Prędkość i adres muszą być takie same jak w menu CAT radia. Zamknij inne programy używające portu (agent, WSJT-X, flrig).')}
              </div>
              <button type="button" className="btn btn--secondary btn--sm" style={{ width: '100%' }} onClick={connect} disabled={detecting}>
                <Icon name="plug" size={14} /> {tx('Połącz radio')}
              </button>
            </>
          )}
          {active && (
            <>
              <div className="webserial__hint">
                {(RIG_PRESETS.find(p => p.id === rig.presetId) || preset).label}
                {rig.frequency != null && <> · <b>{rig.frequency.toFixed(4)} {tx('MHz')}</b> {rig.mode || ''}</>}
                {rig.smeterPeak != null && <> · {sUnitsLabel(rig.smeterPeak)}</>}
                {rig.power != null && <> · {rig.power} W</>}
              </div>
              {(rig.splitTx || rig.repeater) && (
                <div className="webserial__split">
                  <span>{rig.splitTx ? tx('SPLIT · nadawanie {0} MHz', [rig.splitTx.toFixed(4)]) : tx('Przemiennik: przesunięcie i ton ustawione')}</span>
                  <button type="button" className="btn btn--ghost btn--sm" onClick={() => rigSplitOff()}>{tx('Wyłącz')}</button>
                </div>
              )}
              <label className="checkbox-label webserial__tx">
                <input type="checkbox" checked={rig.allowTx} onChange={e => toggleTx(e.target.checked)} />
                {tx('Pozwól loggerowi nadawać (PTT, makra CW)')}
              </label>
              {rig.allowTx && rig.caps?.ptt && (
                <button type="button" className={'btn btn--sm webserial__ptt' + (rig.ptt ? ' webserial__ptt--on' : '')} style={{ width: '100%' }}
                  onClick={() => ptt(!rig.ptt)} aria-pressed={rig.ptt}>
                  {rig.ptt ? tx('● NADAWANIE — kliknij, żeby puścić') : tx('PTT (kliknij, żeby nadawać)')}
                </button>
              )}
              {rig.allowTx && !rig.caps?.cw && <div className="webserial__hint">{tx('To radio nie przyjmuje tekstu CW przez CAT — makra CW idą tylko przez agenta.')}</div>}
              <button type="button" className="btn btn--ghost btn--sm" style={{ width: '100%' }} onClick={() => disconnectRig()}>
                {tx('Rozłącz radio')}
              </button>
            </>
          )}
          {rig.error && <div className="webserial__error">{rig.error}</div>}
        </div>
      )}
    </div>
  );
}

// ── Obrotnica przez USB (Web Serial) ──────────────────────────────────────────
export function WebSerialRotorPanel({ notify }) {
  const rotor = useWebSerialRotor();
  const saved = savedRotorConfig();
  const [presetId, setPresetId] = useState(saved?.presetId || 'gs232');
  const [baud, setBaud] = useState(saved?.baud || 9600);
  const [expanded, setExpanded] = useState(false);
  const active = rotor.status !== 'disconnected';
  if (!isWebSerialSupported()) return null;

  const connect = async () => {
    try { await connectRotor({ presetId, baud: Number(baud) }); }
    catch (e) { notify(tx('Nie udało się otworzyć portu: {0}', [e.message || String(e)]), 'error'); }
  };

  return (
    <div className="webserial webserial--nested">
      <Head status={rotor.status} title={tx('Obrotnica przez USB (bez agenta)')} expanded={expanded} onToggle={() => setExpanded(x => !x)} />
      {(expanded || active) && (
        <div className="webserial__body">
          {!active && (
            <>
              <select className="input webserial__select" value={presetId}
                onChange={e => { setPresetId(e.target.value); setBaud(ROTOR_PRESETS.find(p => p.id === e.target.value).baud); }}>
                {ROTOR_PRESETS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
              <div className="webserial__row">
                <label>{tx('Prędkość')}
                  <select className="input" value={baud} onChange={e => setBaud(Number(e.target.value))}>
                    {ROTOR_BAUDS.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                </label>
              </div>
              <button type="button" className="btn btn--secondary btn--sm" style={{ width: '100%' }} onClick={connect}>
                <Icon name="plug" size={14} /> {tx('Połącz obrotnicę')}
              </button>
            </>
          )}
          {active && (
            <div className="webserial__row">
              <button type="button" className="btn btn--ghost btn--sm" style={{ flex: 1 }} onClick={() => rotorStop()}>{tx('Stop')}</button>
              <button type="button" className="btn btn--ghost btn--sm" style={{ flex: 1 }} onClick={() => disconnectRotor()}>{tx('Rozłącz')}</button>
            </div>
          )}
          {rotor.error && <div className="webserial__error">{rotor.error}</div>}
        </div>
      )}
    </div>
  );
}
